import React, { useState, useEffect } from "react";
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { toast } from 'react-toastify';

const AbandonedCartModal = ({ item, onClose, onDeleted }) => {
    const [activeTab, setActiveTab] = useState("reminder");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [cartDetails, setCartDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendStatus, setSendStatus] = useState(null);
    const [sendMessage, setSendMessage] = useState('');

    useEffect(() => {
        if (!item?.hash) return;

        setIsLoading(true);
        setCartDetails(null);
        setSendStatus(null);

        apiFetch({
            path: addQueryArgs('/easycommerce/v1/dashboard/abandoned-cart', { hash: item.hash }),
        }).then((data) => {
            if (data.success) {
                setCartDetails(data.data);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }, [item?.hash]);

    const handleDelete = () => {
        setIsDeleting(true);
        apiFetch({
            path: '/easycommerce/v1/abandoned-carts/remove',
            method: 'DELETE',
            data: { hash: item.hash },
        }).then(() => {
            setIsDeleting(false);
            toast.success('Abandoned cart deleted successfully');
            onDeleted ? onDeleted() : onClose();
        }).catch(() => {
            toast.error('Failed to delete abandoned cart. Please try again.');
            setIsDeleting(false);
        });
    };

    const handleSendEmail = () => {
        if (!subject.trim() || !message.trim()) return;

        setIsSending(true);
        setSendStatus(null);

        apiFetch({
            path: '/easycommerce/v1/dashboard/abandoned-cart/send-reminder',
            method: 'POST',
            data: { hash: item.hash, subject, message },
        }).then((data) => {
            const sent = data.data?.sent;
            setSendStatus(sent ? 'success' : 'error');
            setSendMessage(data.data?.message || '');
            setIsSending(false);
        }).catch(() => {
            setSendStatus('error');
            setSendMessage('Something went wrong. Please try again.');
            setIsSending(false);
        });
    };

    const formatLastActivity = (updatedAt) => {
        if (!updatedAt) return '—';
        const diff = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000);
        if (diff < 60) return `${diff}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0000003B] backdrop-blur-sm">
            <div className="bg-white rounded-lg w-[850px] relative">
                <button
                    className="absolute top-[-20px] right-[-23px] group w-6 h-6 rounded-full bg-white hover:bg-[#fa4109] transition-colors duration-200 flex items-center justify-center"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path className="fill-[#3C3C42] group-hover:fill-white transition-colors duration-300" fillRule="evenodd" clipRule="evenodd" d="M0.260418 0.260418C0.607642 -0.086806 1.17015 -0.086806 1.51731 0.260418L5 3.7431L8.48269 0.260418C8.82991 -0.086806 9.39241 -0.086806 9.73958 0.260418C10.0868 0.607642 10.0868 1.17015 9.73958 1.51731L6.2569 5L9.73958 8.48269C10.0868 8.82991 10.0868 9.39241 9.73958 9.73958C9.39236 10.0868 8.82985 10.0868 8.48269 9.73958L5 6.2569L1.51731 9.73958C1.17009 10.0868 0.607587 10.0868 0.260418 9.73958C-0.0867505 9.39236 -0.086806 8.82985 0.260418 8.48269L3.7431 5L0.260418 1.51731C-0.086806 1.17009 -0.086806 0.607587 0.260418 0.260418Z" />
                    </svg>
                </button>

                <h2 className="text-xl font-medium text-[#272435] p-4 border-b border-[#EEF0FF]">Abandoned Cart Details</h2>

                <div>
                    <div className="p-4">
                        <div className="grid grid-cols-4 bg-[#F8FAFC] rounded-lg p-4 mb-4 border border-[#EEF0FF]">
                            <div>
                                <p className="text-xs font-normal text-[#7A7A99] uppercase mb-1">Customer Name</p>
                                <p className="text-sm font-normal text-[#121216]">{item.name}</p>
                            </div>
                            <div>
                                <p className="text-xs font-normal text-[#7A7A99] uppercase mb-1">Email Address</p>
                                <p className="text-sm font-normal text-[#121216]">{item.email}</p>
                            </div>
                            <div>
                                <p className="text-xs font-normal text-[#7A7A99] uppercase mb-1">Last Activity</p>
                                <p className="text-sm font-normal text-[#121216]">
                                    {isLoading ? '—' : formatLastActivity(cartDetails?.updated_at)}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-normal text-[#7A7A99] uppercase mb-1">Total Products</p>
                                <p className="text-sm font-bold text-ec-primary">
                                    {isLoading ? '—' : `${cartDetails?.item_count ?? item.items} (${cartDetails?.total ?? item.total})`}
                                </p>
                            </div>
                        </div>

                        <div className="flex bg-[#F3F4F6] rounded-xl p-1">
                            <button
                                className={`flex-1 p-3 text-sm font-normal rounded-lg transition-all duration-200 ${activeTab === "reminder" ? "bg-white text-ec-primary shadow-sm" : "text-[#4F5359]"}`}
                                onClick={() => setActiveTab("reminder")}
                            >
                                Reminder
                            </button>
                            <button
                                className={`flex-1 p-3 text-sm font-normal rounded-lg transition-all duration-200 ${activeTab === "summary" ? "bg-white text-ec-primary shadow-sm" : "text-[#4F5359]"}`}
                                onClick={() => setActiveTab("summary")}
                            >
                                Cart Summary
                            </button>
                        </div>
                    </div>

                    {activeTab === "reminder" && (
                        <div className="p-4 pt-0">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-[#272435] mb-2">Subject Line</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Complete your purchase - Items waiting in your cart!"
                                    className="w-full border border-[#EEF0FF] rounded-lg p-4 text-sm text-[#121216] outline-none focus:border-ec-primary placeholder:text-[#0A0A0A80]"
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-[#272435] mb-2">Email Message</label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Enter your email message..."
                                    rows={7}
                                    className="w-full border border-[#EEF0FF] rounded-lg px-4 py-3 text-sm text-[#121216] outline-none focus:border-ec-primary resize-none placeholder:text-[#0A0A0A80]"
                                />
                            </div>

                            <p className="text-xs text-[#6A7282] mb-4">Use personalization tags to customize the email for each customer</p>

                            {sendStatus === 'success' && (
                                <p className="text-sm text-green-600 mb-3">{sendMessage}</p>
                            )}
                            {sendStatus === 'error' && (
                                <p className="text-sm text-red-500 mb-3">{sendMessage}</p>
                            )}

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="px-6 py-3 text-sm font-medium text-red-500 border border-red-400 rounded-lg hover:bg-red-50 duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                </button>
                                <button
                                    onClick={handleSendEmail}
                                    disabled={isSending || !subject.trim() || !message.trim()}
                                    className="px-6 py-3 text-sm font-medium text-white bg-ec-primary rounded-lg hover:opacity-90 duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSending ? 'Sending...' : 'Send Email'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === "summary" && (
                        <div className="mb-4">
                            <h3 className="text-lg font-medium text-[#3C3C42] mb-4 pl-4">Cart Items</h3>

                            <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] p-4 bg-[#F7F7F7] font-medium">
                                <p className="text-sm text-[#3C3C42]">Product</p>
                                <p className="text-sm text-[#3C3C42] text-center">Variant</p>
                                <p className="text-sm text-[#3C3C42] text-center">Quantity</p>
                                <p className="text-sm text-[#3C3C42] text-center">Price</p>
                                <p className="text-sm text-[#3C3C42] text-center">Total</p>
                            </div>

                            {isLoading ? (
                                <div className="px-4 py-6 text-sm text-[#7A7A99] text-center">Loading cart items...</div>
                            ) : (cartDetails?.items ?? []).length === 0 ? (
                                <div className="px-4 py-6 text-sm text-[#7A7A99] text-center">No items found.</div>
                            ) : (
                                (cartDetails.items).map((cartItem, index) => (
                                    <div key={index} className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr] px-4 py-2.5 border-b border-[#F0EDFF] items-center">
                                        <div className="flex items-center gap-3">
                                            {cartItem.image ? (
                                                <img src={cartItem.image} alt={cartItem.name} className="w-[48px] h-[48px] rounded-[10px] object-cover" />
                                            ) : (
                                                <div className="w-[48px] h-[48px] rounded-[10px] bg-[#F3F3FF]" />
                                            )}
                                            <p className="text-sm text-[#121216]">{cartItem.name}</p>
                                        </div>
                                        <p className="text-sm text-[#7A7A99] text-center">{cartItem.variant || '—'}</p>
                                        <p className="text-sm text-[#121216] text-center">{cartItem.quantity}</p>
                                        <p className="text-sm text-[#121216] text-center">{cartItem.price}</p>
                                        <p className="text-sm text-[#121216] text-center">{cartItem.total}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AbandonedCartModal;
